'use client';

import React, { FC } from 'react';
import { TypeEditPaymentMethodPageFields } from '@/core/generated-types';
import { Errors, GenExternalLink, GenNavTile } from '@/components';

import { findApplicableErrorDetails } from '@/components/common/Errors/utils';
import { usePageData } from '@/context/PageDataProvider';
import { useErrorDirectives } from '@/context/ErrorDirectiveContext';

import { TypePaymentMethodPageData } from '@/pages/[locale]/account/settings/payment-method/[slug]';
import usePaymentMethods from '@/lib/api/productCatalog/hooks/usePaymentMethods';
import useSubscriptions from '@/lib/api/subscriptionManager/hooks/useSubscriptions';
import ReferralBalance from '../AccountSettingsPageTiles/components/ReferralBalance';
import LineAndBlock from '@/components/checkout/Skeletons/LineAndBlock';
import { TypeAddressRecord } from '@/lib/api/accountManager/types';
import generateTranslationsObject from '@/utils/translations/generateTranslationObject';
import { constructLegacyCustomerPortalLink } from '@/utils/shopSubdomain';
import useLocale from '@/hooks/useLocale';
import { getLocalizedPaymentLabel } from './utils';

export type EditPaymentMethodTranslations = {
  billing_address: string;
  changeCta: string;
  editCta: string;
  payment_method_sepa_label: string;
  payment_method_babylist_label: string;
  payment_method_card_label: string;
  default: string;
};

const EditPaymentMethodPage: FC<TypeEditPaymentMethodPageFields> = ({
  referralCredit,
  translations,
}) => {
  const pageData = usePageData() as TypePaymentMethodPageData;
  const locale = useLocale();
  const { data: paymentMethods, loading: paymentMethodsLoading } =
    usePaymentMethods({
      userUuid: pageData.user.uuid,
    });

  const { data: initialSubscriptions, loading: subscriptionsLoading } =
    useSubscriptions({
      userUuid: pageData.user.uuid,
    });

  const { errorDirectives } = useErrorDirectives();
  const { errors, heading } = findApplicableErrorDetails(errorDirectives);
  // We just show the primary payment method on this page.
  const paymentMethod = paymentMethods?.find((method) => method.is_primary);

  if (!pageData.user) {
    return <Errors errors={errors} heading={heading} />;
  }

  if (paymentMethodsLoading || subscriptionsLoading || !paymentMethod) {
    return (
      <LineAndBlock
        key={`skeleton-block`}
        numberOfLines={1}
        numberOfBlocks={3}
        blockClassName="!h-14 !rounded-sm"
      />
    );
  }

  const billingAddress: TypeAddressRecord | undefined =
    initialSubscriptions?.reduce<TypeAddressRecord | undefined>(
      (found, subscription) => {
        if (found) return found;
        return pageData.user.addresses.find(
          (address) => address.uuid === subscription.billing_address_uuid,
        );
      },
      undefined,
    );

  const t = generateTranslationsObject(
    translations?.fields,
  ) as EditPaymentMethodTranslations;

  return (
    <div data-testid="edit-payment-method" className="max-w-[900px] lg:mb-20">
      <GenNavTile
        title={getLocalizedPaymentLabel(paymentMethod, t)}
        titleClasses="font-normal !text-base md:!text-lg"
        badge={{
          text: t.default,
          badgeColor: 'Lime',
        }}
        titleDecorator={
          <GenExternalLink
            href={constructLegacyCustomerPortalLink(locale, [
              pageData.user.uuid,
              'account-settings',
              'payment-method',
              paymentMethod.uuid,
              'edit-card',
            ])}
            cta={t.editCta}
          />
        }
      >
        {billingAddress && (
          <div className="flex items-start justify-between">
            <address className="not-italic" data-testid="billing-address">
              <p className="font-bold text-lg">{t.billing_address}</p>
              <p>{`${billingAddress.first_name} ${billingAddress.last_name}`}</p>
              <p>{billingAddress.line1}</p>
              {billingAddress.line2 && <p>{billingAddress.line2}</p>}
              <p>
                {billingAddress.city}, {billingAddress.state}{' '}
                {billingAddress.zip_code}
              </p>
              <p>{billingAddress.country}</p>
            </address>
            <GenExternalLink
              href={constructLegacyCustomerPortalLink(locale, [
                pageData.user.uuid,
                'account-settings',
                'payment-method',
                paymentMethod.uuid,
                'edit-billing-address',
              ])}
              cta={t.changeCta}
            />
          </div>
        )}
      </GenNavTile>

      {referralCredit && (
        <GenNavTile
          classes="mt-16 lg:mt-10"
          title={referralCredit.fields.label}
        >
          <ReferralBalance
            label={referralCredit.fields.label}
            description={referralCredit.fields.description}
            withoutReferralDescription={
              referralCredit.fields.withoutReferralDescription
            }
          />
        </GenNavTile>
      )}
    </div>
  );
};
export default React.memo(EditPaymentMethodPage);
